import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Badge, Table, Button, Modal, Form,
  Spinner, Alert, Accordion,
} from 'react-bootstrap';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt, faDownload, faFileExcel, faFilePdf, faSyncAlt,
  faChartBar, faUsers, faArrowUp, faArrowDown, faBalanceScale,
  faEdit, faSave, faTimes,
} from '@fortawesome/free-solid-svg-icons';
import {
  getReportAnnuale, getSociSAS, aggiornaSocioSAS,
  exportReportExcel, exportReportPdf,
} from '../../services/api';

const ANNO_CORRENTE = new Date().getFullYear();
const fmt = (v) => `€ ${parseFloat(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => `${parseFloat(v || 0).toFixed(1)}%`;

const CAT_LABELS = {
  acquisto_film: 'Acquisto film',
  acquisto_musica: 'Acquisto musica',
  materiali_servizi_riversamenti: 'Materiali / Servizi riversamenti',
  spese_imballaggio: 'Spese imballaggio',
  marketing_pubblicita: 'Marketing e pubblicità',
  spese_spedizioni: 'Spese spedizioni',
  commissioni_marketplace: 'Commissioni marketplace',
  commissioni_bancarie: 'Commissioni bancarie',
};

// ─── Modal modifica socio ────────────────────────────────────────────────────

function SocioModal({ socio, onClose, onSave }) {
  const [form, setForm] = useState({
    nome: socio.nome || '',
    cognome: socio.cognome || '',
    codice_fiscale: socio.codice_fiscale || '',
    quota_capitale_perc: socio.quota_capitale_perc ?? 50,
    quota_utili_perc: socio.quota_utili_perc ?? 50,
    note: socio.note || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await aggiornaSocioSAS(socio.id, {
        ...form,
        quota_capitale_perc: parseFloat(form.quota_capitale_perc),
        quota_utili_perc: parseFloat(form.quota_utili_perc),
      });
      toast.success('Dati socio aggiornati');
      onSave();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          <FontAwesomeIcon icon={faEdit} className="me-2" />
          Modifica socio — {socio.nome}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-2">
          <Col md={6}><Form.Label className="small">Nome</Form.Label>
            <Form.Control size="sm" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} /></Col>
          <Col md={6}><Form.Label className="small">Cognome</Form.Label>
            <Form.Control size="sm" value={form.cognome} onChange={e => setForm(f => ({ ...f, cognome: e.target.value }))} /></Col>
          <Col md={6}><Form.Label className="small">Codice fiscale</Form.Label>
            <Form.Control size="sm" value={form.codice_fiscale} onChange={e => setForm(f => ({ ...f, codice_fiscale: e.target.value }))} /></Col>
          <Col md={3}><Form.Label className="small">Quota capitale %</Form.Label>
            <Form.Control size="sm" type="number" step="0.1" value={form.quota_capitale_perc}
              onChange={e => setForm(f => ({ ...f, quota_capitale_perc: e.target.value }))} /></Col>
          <Col md={3}><Form.Label className="small">Quota utili %</Form.Label>
            <Form.Control size="sm" type="number" step="0.1" value={form.quota_utili_perc}
              onChange={e => setForm(f => ({ ...f, quota_utili_perc: e.target.value }))} /></Col>
          <Col md={12}><Form.Label className="small">Note</Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}><FontAwesomeIcon icon={faTimes} className="me-1" />Annulla</Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : <FontAwesomeIcon icon={faSave} className="me-1" />}Salva
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Sezione KPI box ────────────────────────────────────────────────────────

function KpiBox({ label, value, color, icon }) {
  return (
    <Card className="text-center border-0 py-2" style={{ backgroundColor: `${color}18` }}>
      <div className="small fw-bold" style={{ color }}>
        {icon && <FontAwesomeIcon icon={icon} className="me-1" />}{label}
      </div>
      <div className="fw-bold" style={{ fontSize: '1.1rem', color }}>{value}</div>
    </Card>
  );
}

// ─── Tabella sezione ────────────────────────────────────────────────────────

function SectionTable({ rows, totalLabel, totalValue, accentColor }) {
  return (
    <Table size="sm" bordered className="mb-0">
      <tbody>
        {rows.map(([label, value], i) => (
          <tr key={i}>
            <td className="small">{label}</td>
            <td className="text-end small fw-bold">{fmt(value)}</td>
          </tr>
        ))}
        <tr style={{ backgroundColor: accentColor, color: '#fff' }}>
          <td className="fw-bold">{totalLabel}</td>
          <td className="text-end fw-bold">{fmt(totalValue)}</td>
        </tr>
      </tbody>
    </Table>
  );
}

// ─── Componente principale ──────────────────────────────────────────────────

export default function ReportAnnuale() {
  const [anno, setAnno] = useState(ANNO_CORRENTE - 1); // default anno precedente
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const [editSocio, setEditSocio] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getReportAnnuale(anno);
      setReport(res.data);
    } catch {
      toast.error('Errore nel caricamento report');
    } finally {
      setLoading(false);
    }
  }, [anno]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (tipo) => {
    setExporting(tipo);
    try {
      const fn = tipo === 'excel' ? exportReportExcel : exportReportPdf;
      const res = await fn(anno);
      const ext = tipo === 'excel' ? 'xlsx' : 'pdf';
      const mime = tipo === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_unico_sp_${anno}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${tipo.toUpperCase()} scaricato`);
    } catch {
      toast.error(`Errore export ${tipo}`);
    } finally {
      setExporting('');
    }
  };

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  const ric = report?.ricavi;
  const cos = report?.costi;
  const red = report?.reddito;
  const iva = report?.iva;
  const soci = report?.soci || [];
  const az = report?.azienda || {};

  return (
    <div className="container-fluid py-3">
      {/* Header */}
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
            Report Annuale — Unico SP
          </h4>
          <small className="text-muted">
            {az.denominazione} — P.IVA {az.piva} — IVA trimestrale, competenza standard
          </small>
        </Col>
        <Col xs="auto" className="d-flex gap-2 align-items-center">
          <Form.Select value={anno} onChange={e => setAnno(+e.target.value)} style={{ width: 100 }}>
            {[ANNO_CORRENTE, ANNO_CORRENTE - 1, ANNO_CORRENTE - 2, ANNO_CORRENTE - 3].map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Form.Select>
          <Button variant="outline-secondary" size="sm" onClick={load} title="Ricalcola">
            <FontAwesomeIcon icon={faSyncAlt} />
          </Button>
          <Button variant="outline-success" size="sm" onClick={() => handleExport('excel')}
            disabled={exporting === 'excel'}>
            {exporting === 'excel' ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faFileExcel} />}
            {' '}Excel
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => handleExport('pdf')}
            disabled={exporting === 'pdf'}>
            {exporting === 'pdf' ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faFilePdf} />}
            {' '}PDF
          </Button>
        </Col>
      </Row>

      {!report && !loading && (
        <Alert variant="info">Nessun dato disponibile per {anno}.</Alert>
      )}

      {report && (
        <>
          {/* KPI top */}
          <Row className="g-2 mb-4">
            <Col xs={6} md={3}>
              <KpiBox label="Totale Ricavi" value={fmt(ric?.totale)} color="#198754" icon={faArrowUp} />
            </Col>
            <Col xs={6} md={3}>
              <KpiBox label="Totale Costi" value={fmt(cos?.totale)} color="#dc3545" icon={faArrowDown} />
            </Col>
            <Col xs={6} md={3}>
              <KpiBox label="Reddito Lordo SAS" value={fmt(red?.reddito_lordo)}
                color={red?.reddito_lordo >= 0 ? '#0d6efd' : '#dc3545'} icon={faBalanceScale} />
            </Col>
            <Col xs={6} md={3}>
              <KpiBox label="IVA versata" value={fmt(iva?.totale_iva_versata_anno)} color="#6c757d" />
            </Col>
          </Row>

          <Accordion defaultActiveKey={['0', '1', '2', '3', '4']} alwaysOpen>

            {/* Sezione Soci */}
            <Accordion.Item eventKey="0" className="mb-2">
              <Accordion.Header>
                <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
                <strong>Soci e ripartizione reddito</strong>
              </Accordion.Header>
              <Accordion.Body className="p-2">
                <Table size="sm" bordered responsive>
                  <thead className="table-dark">
                    <tr>
                      <th>Socio</th>
                      <th>Tipo</th>
                      <th>CF</th>
                      <th className="text-end">Quota capitale</th>
                      <th className="text-end">Quota utili</th>
                      <th className="text-end">Prelievi {anno}</th>
                      <th className="text-end">Conferimenti {anno}</th>
                      <th className="text-end">Reddito attribuito</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {soci.map(s => (
                      <tr key={s.id}>
                        <td className="fw-bold">{s.nome} {s.cognome}</td>
                        <td>
                          <Badge bg={s.tipo === 'accomandatario' ? 'primary' : 'secondary'}>
                            {s.tipo}
                          </Badge>
                        </td>
                        <td className="small text-muted">{s.codice_fiscale || '—'}</td>
                        <td className="text-end">{pct(s.quota_capitale_perc)}</td>
                        <td className="text-end">{pct(s.quota_utili_perc)}</td>
                        <td className="text-end text-danger">{fmt(s.prelievi_anno)}</td>
                        <td className="text-end text-success">{fmt(s.conferimenti_anno)}</td>
                        <td className="text-end fw-bold">{fmt(s.reddito_attribuito)}</td>
                        <td>
                          <Button size="sm" variant="outline-secondary"
                            onClick={() => setEditSocio(s)}>
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <small className="text-muted">
                  Il reddito attribuito è calcolato su: Reddito lordo SAS × quota utili %.
                  Da riportare nel Quadro A del Modello SP.
                </small>
              </Accordion.Body>
            </Accordion.Item>

            {/* Sezione Ricavi */}
            <Accordion.Item eventKey="1" className="mb-2">
              <Accordion.Header>
                <FontAwesomeIcon icon={faArrowUp} className="me-2 text-success" />
                <strong>Ricavi — {fmt(ric?.totale)}</strong>
                <small className="text-muted ms-2">(criterio cassa)</small>
              </Accordion.Header>
              <Accordion.Body className="p-2">
                <Row className="g-3">
                  <Col md={6}>
                    <h6 className="small text-muted text-uppercase mb-2">Per canale (ordini)</h6>
                    <SectionTable
                      rows={Object.entries(ric?.da_ordini?.per_canale || {}).map(([k, v]) => [k, v])}
                      totalLabel="Subtotale vendite"
                      totalValue={ric?.da_ordini?.totale_imponibile}
                      accentColor="#198754"
                    />
                  </Col>
                  <Col md={6}>
                    <h6 className="small text-muted text-uppercase mb-2">Altre fonti</h6>
                    <SectionTable
                      rows={[
                        ['Corrispettivi negozio', ric?.da_corrispettivi?.totale_imponibile],
                        ['Altri incassi', ric?.altri_incassi],
                      ]}
                      totalLabel="TOTALE RICAVI"
                      totalValue={ric?.totale}
                      accentColor="#198754"
                    />
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>

            {/* Sezione Costi */}
            <Accordion.Item eventKey="2" className="mb-2">
              <Accordion.Header>
                <FontAwesomeIcon icon={faArrowDown} className="me-2 text-danger" />
                <strong>Costi deducibili — {fmt(cos?.totale)}</strong>
                <small className="text-muted ms-2">(criterio cassa)</small>
              </Accordion.Header>
              <Accordion.Body className="p-2">
                <Row className="g-3">
                  <Col md={6}>
                    <h6 className="small text-muted text-uppercase mb-2">Da fatture passive pagate</h6>
                    <SectionTable
                      rows={Object.entries(cos?.da_fatture_passive?.per_categoria || {})
                        .map(([k, v]) => [CAT_LABELS[k] || k, v])}
                      totalLabel="Subtotale fatture"
                      totalValue={cos?.da_fatture_passive?.totale}
                      accentColor="#dc3545"
                    />
                  </Col>
                  <Col md={6}>
                    <h6 className="small text-muted text-uppercase mb-2">Da scritture contabili</h6>
                    <SectionTable
                      rows={[
                        ['Rimborsi spese soci', cos?.da_scritture?.rimborsi_spese],
                        ['Altre uscite', cos?.da_scritture?.altre_uscite],
                      ]}
                      totalLabel="TOTALE COSTI"
                      totalValue={cos?.totale}
                      accentColor="#dc3545"
                    />
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>

            {/* Sezione Reddito */}
            <Accordion.Item eventKey="3" className="mb-2">
              <Accordion.Header>
                <FontAwesomeIcon icon={faBalanceScale} className="me-2 text-primary" />
                <strong>Reddito d'impresa — {fmt(red?.reddito_lordo)}</strong>
              </Accordion.Header>
              <Accordion.Body className="p-2">
                <Row className="g-2 mb-3">
                  <Col md={4}>
                    <div className="border rounded p-2 text-center">
                      <div className="small text-muted">Totale ricavi</div>
                      <div className="fs-5 fw-bold text-success">{fmt(ric?.totale)}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="border rounded p-2 text-center">
                      <div className="small text-muted">Totale costi deducibili</div>
                      <div className="fs-5 fw-bold text-danger">{fmt(cos?.totale)}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="border rounded p-2 text-center"
                      style={{ backgroundColor: red?.reddito_lordo >= 0 ? '#e8f4e8' : '#fde8e8' }}>
                      <div className="small text-muted">Reddito lordo SAS</div>
                      <div className={`fs-5 fw-bold ${red?.reddito_lordo >= 0 ? 'text-success' : 'text-danger'}`}>
                        {fmt(red?.reddito_lordo)}
                      </div>
                    </div>
                  </Col>
                </Row>
                <h6 className="small text-muted text-uppercase">Ripartizione per socio (Quadro A)</h6>
                <Table size="sm" bordered>
                  <thead className="table-dark">
                    <tr>
                      <th>Socio</th>
                      <th className="text-end">Quota utili</th>
                      <th className="text-end">Reddito attribuito</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soci.map(s => (
                      <tr key={s.id}>
                        <td>{s.nome} {s.cognome} <Badge bg={s.tipo === 'accomandatario' ? 'primary' : 'secondary'} className="ms-1" style={{ fontSize: '0.7rem' }}>{s.tipo}</Badge></td>
                        <td className="text-end">{pct(s.quota_utili_perc)}</td>
                        <td className="text-end fw-bold">{fmt(s.reddito_attribuito)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>

            {/* Sezione IVA */}
            <Accordion.Item eventKey="4" className="mb-2">
              <Accordion.Header>
                <FontAwesomeIcon icon={faChartBar} className="me-2 text-secondary" />
                <strong>IVA trimestrale — riepilogo anno</strong>
              </Accordion.Header>
              <Accordion.Body className="p-2">
                <Table size="sm" bordered responsive>
                  <thead className="table-dark">
                    <tr>
                      <th>Trimestre</th>
                      <th className="text-end">IVA debito</th>
                      <th className="text-end">IVA credito</th>
                      <th className="text-end">Da versare</th>
                      <th className="text-center">Stato</th>
                      <th className="text-end">Versato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(iva?.dettaglio_trimestri || []).map((t, i) => (
                      <tr key={i}>
                        <td className="fw-bold">{t.trimestre}</td>
                        <td className="text-end text-danger">{fmt(t.iva_debito)}</td>
                        <td className="text-end text-success">{fmt(t.iva_credito)}</td>
                        <td className="text-end fw-bold">{fmt(t.saldo)}</td>
                        <td className="text-center">
                          <Badge bg={t.stato === 'versata' ? 'success' : t.stato === 'confermata' ? 'primary' : 'secondary'}>
                            {t.stato}
                          </Badge>
                        </td>
                        <td className="text-end">{fmt(t.versato)}</td>
                      </tr>
                    ))}
                    <tr className="table-dark">
                      <td className="fw-bold">TOTALE ANNO</td>
                      <td className="text-end fw-bold">{fmt(iva?.totale_iva_debito_anno)}</td>
                      <td className="text-end fw-bold">{fmt(iva?.totale_iva_credito_anno)}</td>
                      <td></td>
                      <td></td>
                      <td className="text-end fw-bold">{fmt(iva?.totale_iva_versata_anno)}</td>
                    </tr>
                  </tbody>
                </Table>
                {iva?.credito_riportato_anno_successivo > 0 && (
                  <Alert variant="info" className="mt-2 mb-0 small">
                    Credito IVA riportato all'anno successivo: <strong>{fmt(iva.credito_riportato_anno_successivo)}</strong>
                  </Alert>
                )}
              </Accordion.Body>
            </Accordion.Item>

          </Accordion>
        </>
      )}

      {editSocio && (
        <SocioModal
          socio={editSocio}
          onClose={() => setEditSocio(null)}
          onSave={() => { setEditSocio(null); load(); }}
        />
      )}
    </div>
  );
}
